import {
  EntityInfoNameLine,
  H2,
  Input,
  LabeledLine,
  PageContainer,
  PageHeader,
  SectionContainer,
  Textarea,
  getImagePortraitProps,
} from "@touche-lts/panel-ui/dist";
import { IconInfoSmall, IconStores } from "assets/icons";
import cl from "classnames";
import { observer } from "mobx-react-lite";
import { useRootStore } from "mobx-stores/store-provider";
import { useCallback, useEffect, useState } from "react";
import { isArray } from "validate.js";
import styles from "./add-store-page.module.scss";
import { AddStorePageProps } from "./add-store-page.types";
import { RadioButton } from "./radio-button/radio-button";
import ShippingField from "./shipping-field/shipping-field";
import { ShopifyDetail } from "./shopify-detail/shopify-detail";

const AddStorePageComponent = (props: AddStorePageProps) => {
  const {
    className,
    style,
    history: { push },
  } = props;

  const {
    addStorePageStore: {
      store,
      onStorePropsChange,
      onPaymentDetailsChange,
      onDiscardClick,
      onAddStoreClick,
      validationErrors,
    },
  } = useRootStore();

  // Determine integration type based on which fields have values
  const getIntegrationType = useCallback((): "old" | "new" | null => {
    const hasOldFields =
      store.shopifyAccessToken || store.shopifyStorefrontToken;
    const hasNewFields = store.clientId;

    if (hasOldFields && !hasNewFields) return "old";
    if (hasNewFields && !hasOldFields) return "new";
    if (!hasOldFields && !hasNewFields) return null;
    return null; // Both have values - shouldn't happen
  }, [store.shopifyAccessToken, store.shopifyStorefrontToken, store.clientId]);

  // Local state for selected integration type
  const [selectedIntegrationType, setSelectedIntegrationType] = useState<
    "old" | "new"
  >("old");

  // Update selected type when store changes
  useEffect(() => {
    const type = getIntegrationType();
    if (type) {
      setSelectedIntegrationType(type);
    }
  }, [getIntegrationType]);

  const handleIntegrationTypeChange = (integrationType: "old" | "new") => {
    setSelectedIntegrationType(integrationType);

    // Clear fields that are not relevant to the new integration type
    if (integrationType === "old") {
      // Clear new integration specific fields (set to null)
      onStorePropsChange({
        clientId: null,
      });
    } else {
      // Clear old integration specific fields (set to null)
      onStorePropsChange({
        shopifyAccessToken: null,
        shopifyStorefrontToken: null,
      });
    }
  };

  return (
    <PageContainer
      className={cl(className, styles.thinScroll, styles.overflowAuto)}
      style={style}
    >
      <PageHeader
        className={styles.pageHeader}
        navSegments={[
          {
            icon: IconStores,
            label: "Stores",
            iconProps: { style: { marginBottom: -5 } },
            onClick: () => push("/stores"),
          },
          {
            label: "Add new",
          },
        ]}
        buttons={[
          {
            children: "Discard",
            preset: "secondary",
            onClick: onDiscardClick,
          },
          {
            children: "Add",
            preset: "primary",
            onClick: onAddStoreClick,
          },
        ]}
      />
      <SectionContainer extraPaddingBottom>
        <EntityInfoNameLine
          editMode
          imageProps={getImagePortraitProps(null, "image")}
          entityName={store.name}
          nameInputProps={{
            placeholder: "Store name",
            value: store.name,
            onChange: (name) => onStorePropsChange({ name }),
            errorMessage: validationErrors?.name?.[0],
          }}
        />

        <div className={styles.proportionFlexBox}>
          <div>
            <LabeledLine className={styles.inputLine} label="Email">
              <Input
                className={styles.input}
                placeholder="Email"
                autoComplete="off"
                autoCapitalize="none"
                type="email"
                value={store.email}
                onChange={(email) => onStorePropsChange({ email })}
                errorMessage={validationErrors?.email?.[0]}
              />
            </LabeledLine>
            <LabeledLine className={styles.inputLine} label="Website">
              <Input
                className={styles.input}
                placeholder="Website"
                autoComplete="off"
                autoCapitalize="none"
                type="url"
                value={store.websiteUrl}
                onChange={(websiteUrl) => onStorePropsChange({ websiteUrl })}
                errorMessage={validationErrors?.websiteUrl?.[0]}
              />
            </LabeledLine>
            <LabeledLine className={styles.inputLine} label="Gemini API Key">
              <Input
                className={styles.input}
                placeholder="Gemini API Key"
                autoComplete="off"
                autoCapitalize="none"
                type="text"
                value={store.geminiKey}
                onChange={(geminiKey) => onStorePropsChange({ geminiKey })}
                errorMessage={validationErrors?.geminiKey?.[0]}
              />
            </LabeledLine>
            <span
              style={{
                maxWidth: "532px",
                fontSize: "12px",
                lineHeight: "1.5",
                color: "#666",
                marginTop: "4px",
                display: "block",
                textAlign: "left",
              }}
            >
              If you want to use your own Gemini key for your projects pls add
              here. Instead we will use our default keys and bill you for it.
            </span>
          </div>
          <div>
            <LabeledLine
              className={cl(styles.inputLine, styles.textareaInputLine)}
              alignItems="stretch"
              label="Description"
            >
              <Textarea
                placeholder="Describe this store..."
                value={store.info}
                onChange={(info) => onStorePropsChange({ info })}
                errorMessage={validationErrors?.info?.[0]}
              />
            </LabeledLine>
          </div>
        </div>
        <H2 className={cl(styles.sectionHeading, styles.flexGap1)}>
          Payment details{" "}
          <IconInfoSmall title="Some info about payment details" />
        </H2>
        <div className={styles.proportionFlexBox}>
          <LabeledLine className={styles.inputLine} label="Username">
            <Input
              className={styles.input}
              placeholder="Username"
              autoComplete="off"
              type="username"
              value={store.paymentDetails.username}
              onChange={(username) => onPaymentDetailsChange({ username })}
              errorMessage={validationErrors?.["paymentDetails.username"]?.[0]}
            />
          </LabeledLine>
          <LabeledLine className={styles.inputLine} label="Password">
            <Input
              className={styles.input}
              placeholder="Password"
              autoComplete="off"
              autoCapitalize="none"
              type="password"
              security="password"
              value={store.paymentDetails.password}
              onChange={(password) => onPaymentDetailsChange({ password })}
              errorMessage={validationErrors?.["paymentDetails.password"]?.[0]}
            />
          </LabeledLine>
        </div>
        <div className={styles.proportionFlexBox}>
          <LabeledLine className={styles.inputLine} label="Business ID">
            <Input
              className={styles.input}
              placeholder="Business ID"
              autoComplete="off"
              type="text"
              value={store.paymentDetails.businessId}
              onChange={(businessId) => onPaymentDetailsChange({ businessId })}
              errorMessage={
                validationErrors?.["paymentDetails.businessId"]?.[0]
              }
            />
          </LabeledLine>
          <LabeledLine className={styles.inputLine} label="Token">
            <Input
              className={styles.input}
              placeholder="Token"
              autoComplete="off"
              autoCapitalize="none"
              type="text"
              value={store.paymentDetails.token}
              onChange={(token) => onPaymentDetailsChange({ token })}
              errorMessage={validationErrors?.["paymentDetails.token"]?.[0]}
            />
          </LabeledLine>
        </div>

        {/* Shopify Integration */}
        <H2 className={cl(styles.sectionHeading, styles.flexGap2)}>
          Shopify details{" "}
          <IconInfoSmall title="Some info about shopify details" />
        </H2>

        {/* Shopify Integration Type Selector */}
        <div className={styles.integrationTypeSelector}>
          <div className={styles.integrationOptions}>
            <div className={styles.radioButtonContainer}>
              <RadioButton
                selected={selectedIntegrationType === "old"}
                onClick={() => handleIntegrationTypeChange("old")}
                label="Old Integration (Admin API)"
              />
              <span className={styles.radioDescription}>
                Requires Admin API access token and Storefront token
              </span>
            </div>
            <div className={styles.radioButtonContainer}>
              <RadioButton
                selected={selectedIntegrationType === "new"}
                onClick={() => handleIntegrationTypeChange("new")}
                label="New Integration (OAuth)"
              />
              <span className={styles.radioDescription}>
                Requires Client ID for OAuth authentication (Available from
                January 1, 2025)
              </span>
            </div>
          </div>
        </div>

        <div className={styles.proportionFlexBox}>
          <div>
            <ShopifyDetail
              integrationType={selectedIntegrationType}
              store={store}
              onStorePropsChange={onStorePropsChange}
              validationErrors={validationErrors}
            />
          </div>
        </div>

        {/* Shipping Options Section */}
        <ShippingField
          shippings={isArray(store.shippings) ? store.shippings : []}
          onShippingsChange={(shippings) => onStorePropsChange({ shippings })}
        />
      </SectionContainer>

      {/* <UsersInShopSection
        extraPaddingBottom
        style={{ marginTop: 20 }}
        addedUsers={addedUsersToStore}
        onRemoveUserClick={onRemoveUserClick}
      /> */}
    </PageContainer>
  );
};

const AddStorePage = observer(AddStorePageComponent);
export { AddStorePage };
